// ReturnItemsDialog — sending received goods back to the vendor, from the order
// they arrived on.
//
// ── WHY THE BOX IS THE UNIT, AND NOT THE LINE ────────────────────────────────
// A PO line is a promise: "300 of these". A receipt box is a physical thing on a
// shelf, with a barcode on it and a location under it. The person doing this
// walks to a shelf, picks a carton and counts out of it, so the form is a list
// of boxes with a number beside each. Each row is strictly one ITEM in a box
// (receipt_items), shown by its box's barcode, and the item id is what is sent. Returning "40 off line 3" would leave the
// question of WHICH forty unanswered, and stock cannot move without an answer.
//
// ── ONE REASON FOR THE WHOLE ACTION ──────────────────────────────────────────
// Every box in one submission shares one reason and one "what happened"
// sentence. That is not a simplification: a return is one conversation with one
// vendor about one problem, and a form asking for a reason per box would collect
// the same sentence four times. Two different problems are two trips through
// this dialog.
//
// Nothing here asks whether money is coming back. At raise time nobody knows:
// the vendor decides, and their answer is recorded as Replaced or Refunded on
// the Returns page.
//
// ── WHERE THE STOCK GOES ─────────────────────────────────────────────────────
// Submitting moves the named quantities out of their shelf location and into the
// QC hold location, immediately, in one transaction. It stays there until
// somebody confirms what the vendor did (replaced or refunded), because until
// then the units are neither ours to build with nor gone.
//
// `quantity_received` on the PO line is NEVER touched. What arrived, arrived.
// The units land in the line's `quantity_returned` instead, which is what the
// order's Remaining column and the line's status are built on.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { buttonClass } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import { SkeletonGroup, Skeleton } from '../components/ui/Skeleton';
import { Field } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import DrawerShell from '../Receiving/DrawerShell';
import ItemName from '../components/ItemName';
import { modalLabel } from '../components/review/fieldStyles';
import ManifestChoice from './BoardReturns/ManifestChoice';
import { useBoardReturnsApi, type ReturnReason } from './BoardReturns/boardReturnsApi';
import { usePoApi, type PoDetail, type PoLine, type PoReceiptItem } from './poApi';

export interface ReturnItemsDialogProps {
  po: PoDetail;
  onClose: () => void;
  onDone: (result: { manifest: { id: string; manifest_number: string } }) => void;
}

function apiErrorText(err: any, fallback: string): string {
  return err?.data?.error?.message || err?.message || fallback;
}

const NOTE = 'text-xs text-on-surface-variant';

/**
 * What is typed in a quantity box, clamped to what the box can actually give.
 *
 * Returns 0 for anything unparseable, INCLUDING an empty field, because an empty
 * field means "none from this one" and the running total has to agree with that
 * the instant it is cleared.
 */
function parseQuantity(raw: string | undefined, max: number): number {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, max);
}

const ReturnItemsDialog: React.FC<ReturnItemsDialogProps> = ({ po, onClose, onDone }) => {
  const poApi = usePoApi();
  const returnsApi = useBoardReturnsApi();

  const [boxes, setBoxes] = useState<PoReceiptItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reasons, setReasons] = useState<ReturnReason[]>([]);

  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [reasonId, setReasonId] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [manifest, setManifest] = useState<'new' | string>('new');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The first quantity field, which is where the work starts. It also carries
  // `data-drawer-autofocus`, so whichever of DrawerShell's mount focus and this
  // effect happens last lands on the same control rather than fighting.
  const firstInput = useRef<HTMLInputElement | null>(null);
  const didFocus = useRef(false);

  useEffect(() => {
    let live = true;
    poApi
      .receiptBoxes(po.id)
      .then((res) => {
        if (!live) return;
        setBoxes(res?.boxes || []);
      })
      .catch(() => {
        if (live) setLoadFailed(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
    // poApi is rebuilt every render by useApi; including it would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [po.id]);

  useEffect(() => {
    let live = true;
    // A preset reason is optional to the API, so a failed lookup costs the
    // dropdown and nothing else. The sentence below it is what is required.
    returnsApi
      .listReasons()
      .then((res) => {
        if (live) setReasons((res?.rows || []).filter((r) => r.is_active !== false));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading || didFocus.current || !firstInput.current) return;
    firstInput.current.focus();
    didFocus.current = true;
  }, [loading]);

  /** Stock lines only: a return moves inventory, and an inspection fee is not
   *  inventory. Service and charge lines have no boxes to pick from. */
  const stockLines = useMemo(
    () => po.lines.filter((l) => l.line_type === 'stock').sort((a, b) => a.line_number - b.line_number),
    [po.lines]
  );

  /** Boxes with nothing left on the shelf are dropped rather than shown at zero:
   *  a row whose only input is permanently 0 is a row that cannot be used. */
  const boxesByLine = useMemo(() => {
    const map = new Map<string, PoReceiptItem[]>();
    boxes.forEach((box) => {
      if (Number(box.quantity_remaining) <= 0) return;
      const key = box.expected_receipt_id || '';
      const list = map.get(key);
      if (list) list.push(box);
      else map.set(key, [box]);
    });
    return map;
  }, [boxes]);

  const picked = useMemo(() => {
    const rows: { receipt_item_id: string; quantity: number }[] = [];
    boxes.forEach((box) => {
      const quantity = parseQuantity(quantities[box.id], Number(box.quantity_remaining));
      if (quantity > 0) rows.push({ receipt_item_id: box.id, quantity });
    });
    return rows;
  }, [boxes, quantities]);

  const totalUnits = picked.reduce((sum, r) => sum + r.quantity, 0);
  const boxCount = picked.length;
  const summary = `${totalUnits} ${totalUnits === 1 ? 'unit' : 'units'} from ${boxCount} ${boxCount === 1 ? 'box' : 'boxes'}`;

  const reasonGiven = reasonText.trim().length > 0;
  // The preset is required, not decoration: a manifest cannot be marked sent
  // while any line has no reason code, so a return raised without one would
  // park stock in QC hold behind a draft nobody can send.
  const reasonPicked = reasonId !== '';
  const canSend = totalUnits > 0 && reasonPicked && reasonGiven && !submitting;

  // Why the pill is off, said where the pill is. A disabled button leaves the
  // tab order, so this sentence is the only way somebody not looking at it
  // learns what it wants.
  const blockedBecause = totalUnits === 0
    ? 'Put a quantity on at least one box.'
    : !reasonPicked
      ? 'Pick a reason.'
      : !reasonGiven
        ? 'Say what happened before sending.'
        : null;

  const setQuantity = useCallback((boxId: string, raw: string) => {
    setQuantities((prev) => ({ ...prev, [boxId]: raw }));
  }, []);

  const handleSend = async () => {
    if (!canSend) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await poApi.returnItems(po.id, {
        manifest,
        reason_id: Number(reasonId),
        reason: reasonText.trim(),
        lines: picked,
      });
      // No setState after this: onDone unmounts the dialog.
      onDone({ manifest: result.manifest });
    } catch (err: any) {
      // The entries stay. A 409 on one box out of six is a correction to make,
      // not a form to fill in again.
      setError(apiErrorText(err, 'The return could not be sent.'));
      setSubmitting(false);
    }
  };

  // Ctrl/Cmd+Enter is "I am done filling this in", from anywhere in the dialog.
  // Same condition as the pill, so the shortcut can never do something the
  // visible control refuses to.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    if (!canSend) return;
    handleSend();
  };

  const renderBox = (box: PoReceiptItem, isFirst: boolean) => {
    const remaining = Number(box.quantity_remaining);
    return (
      <div
        key={box.id}
        className="grid items-center gap-2 rounded-lg bg-surface-container-high px-3 py-2 sm:grid-cols-[minmax(0,1fr)_6rem]"
      >
        <div className="min-w-0">
          <p className="truncate text-sm text-on-surface">
            <span className="font-mono">{box.barcode}</span>
            <span className="text-on-surface-variant">
              {` · ${box.location_name || 'No location'}`}
            </span>
          </p>
          {/* One text node, not a span per figure: this line is read as a
              sentence, and splitting it makes "200 of 200" two facts. */}
          <p className={`${NOTE} tabular-nums`}>
            {`${remaining} of ${box.quantity_received} received`}
            {box.lot_number ? ` · lot ${box.lot_number}` : ''}
          </p>
        </div>
        <Field
          ref={isFirst ? firstInput : undefined}
          data-drawer-autofocus={isFirst ? '' : undefined}
          thumb
          className="text-right"
          type="number"
          inputMode="numeric"
          min={0}
          max={remaining}
          step={1}
          placeholder="0"
          value={quantities[box.id] ?? ''}
          onChange={(e) => setQuantity(box.id, e.target.value)}
          aria-label={`Quantity from ${box.barcode}`}
        />
      </div>
    );
  };

  const renderLine = (line: PoLine, firstBoxId: string | null) => {
    const lineBoxes = boxesByLine.get(line.id) || [];
    const empty = lineBoxes.length === 0;
    return (
      <div
        key={line.id}
        data-testid={`return-line-${line.id}`}
        // NO fill of its own. The panel around it is surface-container now, so
        // a fill here would be the same invisible-panel bug one level down. The
        // item name heads the group and the box rows below carry the tone.
        className={`rounded-lg p-3 ${empty ? 'opacity-60' : ''}`}
      >
        <ItemName item={line} size="sm" />
        {empty ? (
          <p className={`mt-1 ${NOTE}`}>Nothing left in stock to return</p>
        ) : (
          <div className="mt-2 space-y-1.5">
            {lineBoxes.map((box) => renderBox(box, box.id === firstBoxId))}
          </div>
        )}
      </div>
    );
  };

  // Computed once for the whole list rather than per row: "the first field" is a
  // fact about the dialog, and asking each row whether it is first would make it
  // a fact about the row.
  const firstBoxId = useMemo(() => {
    for (const line of stockLines) {
      const list = boxesByLine.get(line.id);
      if (list && list.length > 0) return list[0].id;
    }
    return null;
  }, [stockLines, boxesByLine]);

  // The manifest is left out: it preselects the newest draft on its own, and a pick alone sends nothing.
  // A box at 0 is "none from this one", the same as empty, so it is not work either.
  const dirty =
    Object.values(quantities).some((q) => parseQuantity(q, Infinity) > 0) || reasonId !== '' || reasonText.trim() !== '';

  const nothingToReturn = !loading && !loadFailed && firstBoxId === null;

  return (
    <DrawerShell title="Return items to the vendor" size="lg" onClose={onClose} dirty={dirty}>
      <div className="space-y-4" onKeyDown={handleKeyDown}>
        <p className="text-sm text-on-surface-variant">
          Pick how many of each box go back. Stock moves to QC hold now and leaves when the
          vendor replaces or refunds it.
        </p>

        {/* ---------------- What is going back ----------------
            One step ABOVE the drawer card, not level with it: the card is
            surface-container-low, so a panel painted the same tone is a panel
            nobody can see. The ladder on this dialog runs card → panel
            (-container) → box row (-high) → field (-highest). */}
        <Panel on="panel" className="space-y-2">
          {loading && (
            <SkeletonGroup label="Reading what is still in stock" className="space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-9 w-full" />
            </SkeletonGroup>
          )}
          {loadFailed && (
            <FadeIn key="failed">
              <Alert tone="error" variant="text">
                Could not read this order&apos;s boxes. Close this and try again.
              </Alert>
            </FadeIn>
          )}
          {nothingToReturn && (
            <FadeIn key="empty">
              <EmptyState variant="line">Nothing received against this order is still in stock.</EmptyState>
            </FadeIn>
          )}
          {!loading && !loadFailed && (
            <FadeIn key="lines" className="space-y-2">{stockLines.map((line) => renderLine(line, firstBoxId))}</FadeIn>
          )}
        </Panel>

        {/* ---------------- Why, and where it is going ----------------
            One block for the whole action. See the header note on why the reason
            is not per box. */}
        <Panel on="panel" className="space-y-3">
          <div className="block">
            <span className={modalLabel}>Reason</span>
            <Select
              label="Reason (required)"
              required
              placeholder="Pick a reason"
              options={reasons.map((r) => ({ key: String(r.id), label: r.label }))}
              value={reasonId === '' ? null : String(reasonId)}
              onChange={setReasonId}
            />
          </div>

          <label className="block">
            <span className={modalLabel}>What happened</span>
            <Field
              thumb
              type="text"
              // 200, the same cap as the manifest line it lands on, so the claim
              // and the slip read the same sentence.
              maxLength={200}
              aria-required="true"
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Half the reel came in with bent pins."
            />
          </label>

          <ManifestChoice
            vendorId={po.vendor?.id || null}
            value={manifest}
            onChange={setManifest}
          />
        </Panel>

        <Reveal show={!!error}>
          <Alert tone="error" variant="text">{error}</Alert>
        </Reveal>

        {/* ---------------- The summary bar ---------------- */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-surface-container px-4 py-3">
          <p className="text-sm text-on-surface-variant">
            <span className="tabular-nums text-on-surface">{summary}</span>
          </p>
          {/* The hint travels WITH the pills, at every width. Beside the summary
              it wrapped onto its own line on a narrow drawer and ended up
              nowhere near the dimmed button it explains. */}
          <div className="flex flex-wrap items-center justify-end gap-1">
            {blockedBecause && !submitting && (
              <span id="return-blocked-reason" className="mr-1 text-xs text-on-surface-variant">
                {blockedBecause}
              </span>
            )}
            <button type="button" onClick={onClose} className={buttonClass('quiet')}>
              Cancel
            </button>
            {/* The reason the pill is off is ITS description, so a reader landing
                on a dimmed button hears why rather than just that. */}
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              aria-describedby={blockedBecause && !submitting ? 'return-blocked-reason' : undefined}
              className={buttonClass('filled')}
            >
              {submitting ? 'Adding…' : 'Add to manifest'}
            </button>
          </div>
        </div>

        {/* The running total, for somebody who cannot see the bar. Its own node
            rather than aria-live on the bar itself: the bar's text changes shape
            when the hint appears and disappears, and a live region reads the
            whole thing again every time it does. */}
        <p className="sr-only" role="status">
          {summary} selected.
        </p>
      </div>
    </DrawerShell>
  );
};

export default ReturnItemsDialog;
